import React, { useState, useEffect, useMemo } from 'react';
import { Typography, TextField, Button, Grid2, Card, CardContent, LinearProgress, Box, IconButton, Dialog, DialogActions, DialogContent, DialogTitle, DialogContentText, Tooltip, Select, MenuItem, InputLabel, FormControl, Radio, RadioGroup, FormControlLabel, Chip, Stack, Autocomplete, useTheme } from '@mui/material';
import { Edit, Check, Delete, Paid, Add, Close, PeopleAlt, AttachMoney, Category, Person } from '@mui/icons-material';
import { getEventBudgetCategories, createBudgetCategory, updateBudgetCategory, deleteBudgetCategory, updateTotalBudget, updateBudgetSplits, getBudgetSplits, getEventSplitUsers } from '../actions/budget';
import { getEventDetails } from '../actions/events';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import AccessDenied from '../components/AccessDenied';
import { useAlert } from '../contexts/AlertContext';
import { cardGridSx, cardItemSx } from '../utils/cardLayout';
import BudgetGuardrail from '../components/BudgetGuardrail';
import PaymentMilestones from '../components/PaymentMilestones';
import { budgetPercentage } from '../utils/budgetPercentage';

const BudgetTracking = () => {
  const theme = useTheme();
  const { showAlert } = useAlert();

  const capitalizeName = (name) => {
    if (!name) return '';
    return name
      .split(' ') 
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const userName = capitalizeName(Cookies.get('user'));

  const { eventId } = useParams(); // Get eventId from URL params
  const [totalBudget, setTotalBudget] = useState(0);
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [paidBy, setPaidBy] = useState(userName);
  const [expense, setExpense] = useState(0);
  const [advancePaid, setAdvancePaid] = useState(0);
  const [actualPaid, setActualPaid] = useState(0);
  const [editingCategory, setEditingCategory] = useState(null);
  const [visiblePaidForms, setVisiblePaidForms] = useState({});
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [totalExpense, setTotalExpense] = useState(0);
  const [totalPaidSoFar, setTotalPaidSoFar] = useState(0);
  
  // New filter states
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedPaidByFilter, setSelectedPaidByFilter] = useState('');

  // New state for splits
  const [openSplitDialog, setOpenSplitDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [splitType, setSplitType] = useState('equal');
  const [splitUsers, setSplitUsers] = useState([]); // Combined list of system and custom users
  const [customSplits, setCustomSplits] = useState({});
  const [eventSplitUsers, setEventSplitUsers] = useState([]);
  const [newSplitUser, setNewSplitUser] = useState('');
  const [customUsers, setCustomUsers] = useState([]);
  const [accessDenied, setAccessDenied] = useState(false);

  // Fetch categories and total budget on load
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch categories and event details in parallel
        const [fetchedCategories, eventDetails] = await Promise.all([
          getEventBudgetCategories(eventId),
          getEventDetails(eventId)
        ]);
  
        setCategories(fetchedCategories);           // Set categories data
        setTotalBudget(eventDetails.total_budget);   // Set total budget from event details
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    fetchData();
  }, [eventId]);

  // Fetch event guests
  useEffect(() => {
    const fetchGuests = async () => {
      try {
        const splitUsers = await getEventSplitUsers(eventId);
        setEventSplitUsers(splitUsers);
      } catch (error) {
        if (error?.response?.status === 403) {
          setAccessDenied(true);
        } else {
          showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Failed to load data', 'error');
        }
      }
    };
    fetchGuests();
  }, [eventId]);

  const getUniqueFilterValues = (key) => {
    const values = categories.map(category => {
      let value = category[key];
      
      if (!value || value.trim() === '') {
        return key === 'category_name' ? 'Uncategorized' : 'Unassigned';
      }
      return value;
    });

    const uniqueValues = Array.from(new Set(
      values.map(value => value.toLowerCase())
    )).map(value => {
      if (value === 'uncategorized' || value === 'unassigned') {
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
      return capitalizeName(value);
    }).sort();

    return uniqueValues;
  };

  // Get unique users from both event guests and paid by fields
  const getAllAvailableUsers = () => {
    const paidByUsers = Array.from(new Set(categories.map(category => category.paid_by).filter(Boolean)));
    const eventUsers = eventSplitUsers || [];
    
    // Combine and remove duplicates
    const allUsers = [...new Set([...paidByUsers, ...eventUsers])];
    return allUsers.sort();
  };

  // Handle filtering of categories
  const filteredCategories = useMemo(() => {
    return categories.filter(category => {
      const categoryValue = category.category_name || '';
      const paidByValue = category.paid_by || '';

      const categoryMatch = !selectedCategoryFilter ||
        (selectedCategoryFilter === 'Uncategorized' && categoryValue.trim() === '') ||
        (capitalizeName(categoryValue) === selectedCategoryFilter);

      const paidByMatch = !selectedPaidByFilter ||
        (selectedPaidByFilter === 'Unassigned' && paidByValue.trim() === '') ||
        (capitalizeName(paidByValue) === selectedPaidByFilter);

      return categoryMatch && paidByMatch;
    });
  }, [categories, selectedCategoryFilter, selectedPaidByFilter]);

  // Calculate total expenses and paid so far for filtered categories
  useEffect(() => {
    setTotalExpense(filteredCategories.reduce((sum, category) => sum + category.expense, 0));
    setTotalPaidSoFar(filteredCategories.reduce((sum, category) => sum + (category.is_paid ? category.actual_paid : category.advance_paid), 0));
  }, [filteredCategories]);

  // Calculate budget status
  const budgetStatus = {
    totalExpense: totalExpense,
    totalBudget: totalBudget,
    remainingBudget: totalBudget - totalExpense,
    isOverBudget: totalExpense > totalBudget,
    overBudgetAmount: Math.max(0, totalExpense - totalBudget),
    budgetUtilization: totalBudget > 0 ? (totalExpense / totalBudget) * 100 : 0
  };

  const handleBudgetSave = async () => {
    try {
      await updateTotalBudget(eventId, totalBudget);
      setIsEditingBudget(false); // Exit edit mode
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to update total budget', error);
    }
  };

  const clearAddOrEditCateogryForm = () => {
    setOpenFormDialog(false);
    setNewCategory('');
    setNewCategoryName('');
    setExpense(0);
    setAdvancePaid(0);
    setPaidBy(userName);
    setEditingCategory(null);
  };

  // Handle add or edit category
  const handleAddOrEditCategory = async (event, status) => {
    event.preventDefault();

    if (!newCategory.trim()) {
      alert('Expense name is required!');
      return;
    }
    if (expense === '' || !Number.isFinite(Number(expense)) || Number(expense) < 0) {
      showAlert('Enter a non-negative expense amount.', 'error');
      return;
    }
    if (!paidBy) {
      alert('Paid by is required!');
      return;
    }

    // Check if adding this expense would exceed budget
    const newExpenseAmount = parseFloat(expense) || 0;
    const currentTotalExpense = categories.reduce((sum, cat) => {
      if (editingCategory && cat.id === editingCategory.id) {
        return sum; // Exclude the category being edited
      }
      return sum + cat.expense;
    }, 0);
    
    const wouldExceedBudget = (currentTotalExpense + newExpenseAmount) > totalBudget;
    
    if (wouldExceedBudget && Number(totalBudget) > 0) {
      const override = window.confirm(
        `Adding this expense ($${newExpenseAmount.toFixed(2)}) would exceed your budget by $${(currentTotalExpense + newExpenseAmount - totalBudget).toFixed(2)}.\n\nDo you want to continue anyway?`
      );
      if (!override) {
        return;
      }
    }

    const isPaid = status === 'paid';

    const categoryData = {
      name: newCategory,
      expense: parseFloat(expense) || 0,
      advance_paid: parseFloat(advancePaid) || 0,
      category_name: newCategoryName,
      paid_by: paidBy,
      actual_paid: isPaid ? parseFloat(expense) : 0,
      is_paid: isPaid,
      expected_paid: parseFloat(expense) || 0,
    };

    try {
      if (editingCategory) {
        await updateBudgetCategory(editingCategory.id, categoryData);
      } else {
        await createBudgetCategory(eventId, categoryData);
      }
      const updatedCategories = await getEventBudgetCategories(eventId);
      setCategories(updatedCategories);

      clearAddOrEditCateogryForm();
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to save category', error);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteBudgetCategory(categoryToDelete);
      const updatedCategories = await getEventBudgetCategories(eventId);
      setCategories(updatedCategories);

      setOpenDeleteDialog(false);
      setCategoryToDelete(null);
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to delete category', error);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setCategoryToDelete(null);
  };

  const handleDeleteOpen = (categoryId) => {
    setCategoryToDelete(categoryId);
    setOpenDeleteDialog(true);
  };

  const handleEdit = (category) => {
    setNewCategory(category.name);
    setExpense(category.expense);
    setAdvancePaid(category.advance_paid);
    setNewCategoryName(category.category_name);
    setPaidBy(category.paid_by);
    setEditingCategory(category);
    setOpenFormDialog(true);
  };

  const handleSavePaidValues = async (category, actualPaidValue) => {
    try {
      const updatedCategoryData = {
        ...category,
        actual_paid: parseFloat(actualPaidValue) || 0,
        is_paid: true,
        expected_paid: category.expense,
        expense: parseFloat(actualPaidValue) || 0,
      };
      await updateBudgetCategory(category.id, updatedCategoryData);

      const updatedCategories = await getEventBudgetCategories(eventId);
      setCategories(updatedCategories);

      setVisiblePaidForms((prev) => ({ ...prev, [category.id]: false }));
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to save paid values', error);
    }
  };

  // Handle adding a custom user
  const handleAddCustomUser = () => {
    if (!newSplitUser.trim()) return;
    
    const newCustomUser = {
      id: `custom-${Date.now()}`, // Generate a temporary unique ID
      name: newSplitUser.trim(),
      isCustom: true
    };
    
    setCustomUsers([...customUsers, newCustomUser]);
    setSplitUsers([...splitUsers, newCustomUser]);
    setNewSplitUser('');
  };

  // Handle removing a user from split
  const handleRemoveUser = (userId) => {
    setSplitUsers(splitUsers.filter(user => user.id !== userId));
    if (userId.toString().startsWith('custom-')) {
      setCustomUsers(customUsers.filter(user => user.id !== userId));
    }
    // Also remove from splits if exists
    setCustomSplits(prev => {
      const newSplits = { ...prev };
      delete newSplits[userId];
      return newSplits;
    });
  };

  const handleOpenSplitDialog = async (category) => {
    setSelectedCategory(category);
    setOpenSplitDialog(true);
    setSplitType(category.split_type || 'equal');
    setSplitUsers([]);
    setCustomUsers([]);
    setCustomSplits({});
    setNewSplitUser('');

    try {
      const existingSplits = await getBudgetSplits(category.id);
      if (existingSplits && existingSplits.length > 0) {
        // Create split users from existing splits
        const splitUsersList = existingSplits.map(split => ({
          id: split.id.toString(),
          name: split.person_name,
          isCustom: !eventSplitUsers.includes(split.person_name)
        }));

        // Separate custom and system users
        const customUsersList = splitUsersList.filter(user => user.isCustom);
        setCustomUsers(customUsersList);
        setSplitUsers(splitUsersList);
        
        // Set split amounts
        const splits = {};
        existingSplits.forEach(split => {
          splits[split.id] = parseFloat(split.split_amount);
        });
        setCustomSplits(splits);
      }
    } catch (error) {
      console.error('Failed to load existing splits', error);
    }
  };

  // Reset splits
  const handleSplitTypeChange = (event) => {
    setSplitType(event.target.value);
    setCustomSplits({});
  };

  const handleSaveSplits = async () => {
    if (!selectedCategory || splitUsers.length === 0) return;

    try {
      // Transform the splits data into the required format
      let splitsArray = [];
      
      if (splitType === 'equal') {
        const amountPerPerson = selectedCategory.expense / splitUsers.length;
        splitsArray = splitUsers.map(user => ({
          person_name: user.name,
          split_amount: amountPerPerson
        }));
      } else {
        splitsArray = splitUsers.map(user => ({
          person_name: user.name,
          split_amount: parseFloat(customSplits[user.id]) || 0
        }));
      }

      await updateBudgetSplits(selectedCategory.id, {
        splits: splitsArray,
        split_type: splitType
      });
      
      // Refresh categories to get updated data
      const updatedCategories = await getEventBudgetCategories(eventId);
      setCategories(updatedCategories);
      
      setOpenSplitDialog(false);
    } catch (error) {
      showAlert(error?.response?.data?.error || error?.response?.data?.message || error.message || 'Operation failed', 'error');
      console.error('Failed to save splits', error);
    }
  };

  // Handle custom split amount change
  const handleCustomSplitChange = (userId, amount) => {
    setCustomSplits(prev => ({
      ...prev,
      [userId]: parseFloat(amount) || 0
    }));
  };

  if (accessDenied) return <AccessDenied />;

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <BudgetGuardrail limit={totalBudget} expenses={categories} />
      {/* Total Budget Section */}
      <Card sx={{ 
        mt: { xs: 4, sm: 0 },
        mb: 4,
        background: theme.palette.mode === 'dark' 
          ? 'rgba(255, 255, 255, 0.0)'
          : 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        boxShadow: 0,
        border: 0
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 2
          }}>
            <AttachMoney sx={{ 
              fontSize: { xs: 24, sm: 32 }, 
              color: theme.palette.primary.main 
            }} />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.text.primary,
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              Total Budget: {isEditingBudget ? (
                <TextField
                  value={totalBudget || ''}
                  onChange={(e) => setTotalBudget(parseFloat(e.target.value) || 0)}
                  size="small"
                  type="number"
                  sx={{ width: { xs: 100, sm: 120 } }}
                />
              ) : `$${totalBudget.toFixed(2)}`}
              <IconButton 
                size="small" 
                onClick={() => isEditingBudget ? handleBudgetSave() : setIsEditingBudget(true)}
                sx={{ color: theme.palette.primary.main }}
              >
                {isEditingBudget ? <Check /> : <Edit />}
              </IconButton>
              { isEditingBudget && (
                <IconButton 
                  size="small" 
                  onClick={() => setIsEditingBudget(false)}
                  sx={{ color: theme.palette.error.main }}
                >
                  <Close />
                </IconButton>
              )}
            </Typography>
          </Box>

          {/* Total Expense Progress Bar */}
          <Box sx={{ mt: 3, position: 'relative', height: '8px', borderRadius: '4px', overflow: 'hidden', bgcolor: theme.palette.grey[200] }}>
            {budgetStatus.isOverBudget ? (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: theme.palette.error.main,
                  borderRadius: '4px',
                  position: 'absolute',
                }}
              />
            ) : (
              <>
                <Box
                  sx={{
                    width: `${budgetPercentage(totalPaidSoFar, totalBudget)}%`,
                    height: '100%',
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: '4px 0 0 4px',
                    position: 'absolute',
                  }}
                />
                <Box
                  sx={{
                    width: `${budgetPercentage(totalExpense - totalPaidSoFar, totalBudget)}%`,
                    height: '100%',
                    backgroundColor: theme.palette.warning.main,
                    borderRadius: '0 4px 4px 0',
                    position: 'absolute',
                    left: `${budgetPercentage(totalPaidSoFar, totalBudget)}%`,
                  }}
                />
              </>
            )}
          </Box>
          
          {/* Budget Status Display */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {selectedCategoryFilter || selectedPaidByFilter ? 'Filtered Expense' : 'Total Expense'}: ${totalExpense.toFixed(2)} | Paid So Far: ${totalPaidSoFar.toFixed(2)}
            </Typography>
            
            {budgetStatus.isOverBudget && (
              <Typography variant="body2" color="error" sx={{ fontWeight: 'bold', mt: 1 }}>
                OVER BUDGET: ${budgetStatus.overBudgetAmount.toFixed(2)} over limit
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Controls Section */}
      <Box sx={{ mb: 4 }}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, sm: 12, md: 4 }} >
            {/* Add/Edit Category Button */}
            <Button 
              variant="contained" 
              color={budgetStatus.isOverBudget ? "error" : "primary"} 
              startIcon={<Add />} 
              onClick={() => setOpenFormDialog(true)} 
              fullWidth 
              sx={{ 
                height: '56px',
                borderRadius: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {budgetStatus.isOverBudget ? 'Add Expense (Over Budget)' : 'Add New Expense'}
            </Button>
          </Grid2>
          
          {/* Filter Section */}
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                label="Category"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {getUniqueFilterValues('category_name').map((categoryName) => (
                  <MenuItem key={categoryName} value={categoryName}>{categoryName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
          
          <Grid2 size={{ xs: 12, sm: 6, md: 4 }} >
            <FormControl size="medium" fullWidth>
              <InputLabel>Paid By</InputLabel>
              <Select
                value={selectedPaidByFilter}
                onChange={(e) => setSelectedPaidByFilter(e.target.value)}
                label="Paid By"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Paid By</MenuItem>
                {getUniqueFilterValues('paid_by').map((paidByName) => (
                  <MenuItem key={paidByName} value={paidByName}>{paidByName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid2>
        </Grid2>
      </Box>

      {/* Filtered Category List */}
      <Box sx={cardGridSx(230)}>
        {filteredCategories.length === 0 ? (
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Card sx={{ 
              p: { xs: 4, sm: 6 }, 
              textAlign: 'center',
              background: theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)'
                : 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
              borderRadius: 3,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                : '0 4px 20px rgba(0, 0, 0, 0.1)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                gap: 2
              }}>
                <AttachMoney sx={{ 
                  fontSize: 64, 
                  color: theme.palette.primary.main,
                  opacity: 0.7
                }} />
                <Box>
                  <Typography variant="h5" sx={{ 
                    fontWeight: 600, 
                    color: theme.palette.text.primary,
                    mb: 1,
                    fontSize: { xs: '1.25rem', sm: '1.5rem' }
                  }}>
                    {selectedCategoryFilter || selectedPaidByFilter ? 
                      'No expenses found for the selected filters' : 
                      'No expenses added yet'
                    }
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ 
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    lineHeight: 1.5
                  }}>
                    {selectedCategoryFilter || selectedPaidByFilter ? 
                      'Try adjusting your filters to see more results.' : 
                      'Start tracking your event expenses by clicking "Add New Expense" above.'
                    }
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        ) : filteredCategories.map((category) => {
          const isExceeded = category.expected_paid < category.actual_paid;
          const displayCategory = !category.category_name || category.category_name.trim() === '' 
            ? 'Uncategorized' 
            : capitalizeName(category.category_name);
          const displayPaidBy = !category.paid_by || category.paid_by.trim() === ''
            ? 'Unassigned'
            : capitalizeName(category.paid_by);

          return (
            <Box key={category.id} sx={cardItemSx(280)}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 3,
                background: theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.05)'
                  : 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 4px 20px rgba(0, 0, 0, 0.2)'
                  : '0 4px 20px rgba(0, 0, 0, 0.1)',
                border: isExceeded ? '2px solid orange' : category.is_paid ? '2px solid green' : theme.palette.mode === 'dark'
                  ? '1px solid rgba(255, 255, 255, 0.1)'
                  : '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 8px 30px rgba(0, 0, 0, 0.3)'
                    : '0 8px 30px rgba(0, 0, 0, 0.15)',
                }
              }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, minHeight: '1.5rem' }}>
                    {category.name}
                  </Typography>
                  
                  {/* Expense Details Section */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Expense: ${category.expense.toFixed(2)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AttachMoney sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.warning.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Adv. Paid: ${category.advance_paid.toFixed(2)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Person sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Paid By: {displayPaidBy}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Category sx={{ fontSize: { xs: 14, sm: 16 }, mr: 1, color: theme.palette.primary.main }} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        Category: {displayCategory}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Progress Section - Always at bottom */}
                  <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={category.is_paid ? 100 : budgetPercentage(category.advance_paid, category.expense)}
                        sx={{ 
                          height: 6,
                          borderRadius: 3,
                          bgcolor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: category.is_paid ? theme.palette.success.main : theme.palette.primary.main,
                            borderRadius: 3
                          }
                        }}
                      />
                    </Box>

                    <Typography variant="body2" color={isExceeded ? "error" : category.is_paid ? "success" : "text.secondary"} sx={{ fontWeight: 500, mb: 2 }}>
                      {isExceeded ? `Exceeded by $${(category.actual_paid - category.expected_paid).toFixed(2)}` :
                       category.is_paid ? 'Fully Paid' : `${budgetPercentage(category.advance_paid, category.expense).toFixed(2)}% Paid`}
                    </Typography>

                    {/* Action buttons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      pt: 2,
                      borderTop: `1px solid ${theme.palette.divider}`,
                      gap: 0.5
                    }}>
                      <PaymentMilestones budget={category} />
                      {!category.is_paid && (
                        <Tooltip title="Mark as Paid" arrow>
                          <IconButton 
                            size="small"
                            onClick={() => setVisiblePaidForms((prev) => ({ ...prev, [category.id]: !prev[category.id] }), setActualPaid(category.expected_paid))}
                            sx={{ 
                              color: theme.palette.info.main,
                              '&:hover': { backgroundColor: theme.palette.info.main + '20' }
                            }}
                          >
                            <Paid fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Split Expense" arrow>
                        <IconButton 
                          size="small"
                          color={category.has_splits ? "secondary" : "default"} 
                          onClick={() => handleOpenSplitDialog(category)}
                          sx={{ 
                            color: category.has_splits ? theme.palette.secondary.main : theme.palette.text.secondary,
                            '&:hover': { backgroundColor: (category.has_splits ? theme.palette.secondary.main : theme.palette.text.secondary) + '20' }
                          }}
                        >
                          <PeopleAlt fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Expense" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleEdit(category)}
                          sx={{ 
                            color: theme.palette.primary.main,
                            '&:hover': { backgroundColor: theme.palette.primary.main + '20' }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Expense" arrow>
                        <IconButton 
                          size="small"
                          onClick={() => handleDeleteOpen(category.id)}
                          sx={{ 
                            color: theme.palette.error.main,
                            '&:hover': { backgroundColor: theme.palette.error.main + '20' }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {visiblePaidForms[category.id] && (
                    <CardContent sx={{ pt: 0 }}>
                      <TextField 
                        label="Expected Expense" 
                        value={category.expected_paid} 
                        margin="normal" 
                        fullWidth 
                        disabled 
                        slotProps={{ inputLabel: { shrink: true } }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                      <TextField
                        type='number'
                        label="Actual Paid"
                        value={actualPaid}
                        onChange={(e) => setActualPaid(e.target.value)}
                        margin="normal"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      />
                      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                        <Button 
                          variant="contained" 
                          size="small" 
                          onClick={() => handleSavePaidValues(category, actualPaid)}
                          sx={{ borderRadius: 2 }}
                        >
                          Save
                        </Button>
                        <Button 
                          variant="outlined" 
                          size="small" 
                          onClick={() => setVisiblePaidForms((prev) => ({ ...prev, [category.id]: false }))}
                          sx={{ borderRadius: 2 }}
                        >
                          Cancel
                        </Button>
                      </Box>
                    </CardContent>
                  )}
                </CardContent>
              </Card>
            </Box>
          );
        })}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openFormDialog} 
        onClose={() => clearAddOrEditCateogryForm()} 
        fullWidth 
        maxWidth="sm"
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          {editingCategory ? 'Edit Expense' : 'Add New Expense'}
        </DialogTitle>
        <form onSubmit={handleAddOrEditCategory}>
          <DialogContent sx={{ 
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.02)'
              : 'rgba(255, 255, 255, 0.9)',
            pt: 2
          }}>
            <BudgetGuardrail limit={totalBudget} expenses={[
              ...categories.filter(category => category.id !== editingCategory?.id),
              { expense: Number(expense) || 0 },
            ]} />
            <TextField 
              label="Name" 
              value={newCategory} 
              onChange={(e) => setNewCategory(e.target.value)} 
              margin="normal" 
              fullWidth 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <TextField 
              label="Expense" 
              value={expense} 
              onChange={(e) => setExpense(e.target.value)} 
              margin="normal" 
              fullWidth 
              type='number' 
              inputProps={{ min: 0, step: '0.01', max: 999999999 }}
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <TextField 
              label="Advance Paid" 
              value={advancePaid} 
              onChange={(e) => setAdvancePaid(e.target.value)} 
              margin="normal" 
              fullWidth 
              type='number'
              inputProps={{ min: 0, step: '0.01', max: 999999999 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Autocomplete
              freeSolo
              options={categories ? Array.from(new Set(categories.map((category) => category.category_name || 'Other'))) : []}
              value={newCategoryName || ''}
              onChange={(event, newValue) => {
                setNewCategoryName(newValue || '');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  margin="normal"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />
              )}
            />
            <TextField 
              label="Paid By" 
              value={paidBy} 
              onChange={(e) => setPaidBy(e.target.value)} 
              margin="normal" 
              fullWidth 
              required
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </DialogContent>
          <DialogActions sx={{ 
            background: theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.9)',
            p: { xs: 1, sm: 2 },
            gap: { xs: 1, sm: 0 },
            flexDirection: 'row',
            alignItems: 'center'
          }}>
            <Button 
              onClick={() => clearAddOrEditCateogryForm()} 
              color="error"
              sx={{ 
                borderRadius: 2
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={(e) => handleAddOrEditCategory(e, 'paid')} 
              color="success"
              variant="contained"
              sx={{ 
                borderRadius: 2
              }}
            >
              {editingCategory ? 
                'Save and Mark as Paid' : 
                'Add and Mark as Paid'
              }
            </Button>
            <Button 
              onClick={(e) => handleAddOrEditCategory(e, 'unpaid')} 
              color="primary"
              variant="contained"
              sx={{ 
                borderRadius: 2
              }}
            >
              {editingCategory ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel} maxWidth="sm">
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <DialogContentText sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
            Are you sure you want to delete this Expense?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={handleDeleteCancel} 
            color="primary"
            sx={{ 
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error"
            variant="contained"
            sx={{ 
              borderRadius: 2
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Dialog */}
      <Dialog 
        open={openSplitDialog} 
        onClose={() => setOpenSplitDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ 
          fontWeight: 600,
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
        }}>
          Split Expense{selectedCategory ? `: ${selectedCategory.name}` : ''}
        </DialogTitle>
        <DialogContent sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.02)'
            : 'rgba(255, 255, 255, 0.9)',
          pt: 2
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="subtitle1" 
              gutterBottom
              sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}
            >
              Total Amount: ${selectedCategory?.expense.toFixed(2)}
            </Typography>
          </Box>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <RadioGroup row value={splitType} onChange={handleSplitTypeChange}>
              <FormControlLabel 
                value="equal" 
                control={<Radio />} 
                label="Split Equally" 
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              />
              <FormControlLabel 
                value="custom" 
                control={<Radio />} 
                label="Custom Split" 
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              />
            </RadioGroup>
          </FormControl>

          {/* Add new person section */}
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <TextField
              label="Add Person"
              value={newSplitUser}
              onChange={(e) => setNewSplitUser(e.target.value)}
              fullWidth
              placeholder="Enter name"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddCustomUser}
              disabled={!newSplitUser.trim()}
              sx={{ borderRadius: 2 }}
            >
              Add
            </Button>
          </Box>

          {/* System users dropdown */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Add People from Event</InputLabel>
            <Select
              multiple
              value={splitUsers.filter(u => !u.isCustom).map(u => u.name)}
              onChange={(e) => {
                const selectedNames = e.target.value;
                const selectedSystemUsers = selectedNames.map(name => ({
                  id: `system-${name}`,
                  name: name,
                  isCustom: false
                }));
                const currentCustomUsers = splitUsers.filter(u => u.isCustom);
                setSplitUsers([...selectedSystemUsers, ...currentCustomUsers]);
              }}
              label="Add People from Event"
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            >
              {getAllAvailableUsers().map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Selected users chips */}
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="subtitle2" 
              gutterBottom
              sx={{ fontWeight: 600, fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Selected People:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {splitUsers.map((user) => (
                <Chip
                  key={user.id}
                  label={user.name}
                  onDelete={() => handleRemoveUser(user.id)}
                  color={user.isCustom ? "secondary" : "primary"}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                />
              ))}
            </Stack>
          </Box>

          {splitUsers.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {splitType === 'equal' ? (
                <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Each person will pay: ${selectedCategory ? (selectedCategory.expense / splitUsers.length).toFixed(2) : '0.00'}
                </Typography>
              ) : (
                <Box>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem' } }}
                  >
                    Custom Split Amounts:
                  </Typography>
                  {splitUsers.map(user => (
                    <TextField
                      key={user.id}
                      label={user.name}
                      type="number"
                      value={customSplits[user.id] || ''}
                      onChange={(e) => handleCustomSplitChange(user.id, e.target.value)}
                      fullWidth
                      margin="dense"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  ))}
                  <Typography 
                    variant="body2" 
                    color="error" 
                    sx={{ 
                      mt: 1,
                      fontWeight: 600,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    Total: ${Object.values(customSplits).reduce((sum, amount) => sum + (amount || 0), 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          p: { xs: 1, sm: 2 },
          gap: { xs: 1, sm: 0 },
          flexDirection: 'row',
          alignItems: 'center'
        }}>
          <Button 
            onClick={() => setOpenSplitDialog(false)} 
            color="error"
            sx={{ 
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSplits} 
            color="primary"
            variant="contained"
            disabled={splitUsers.length === 0 || 
              (splitType === 'custom' && 
                Object.values(customSplits).reduce((sum, amount) => sum + (amount || 0), 0) !== selectedCategory?.expense)
            }
            sx={{ 
              borderRadius: 2
            }}
          >
            Save Splits
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BudgetTracking;
